/**
 * ReleasesPage — standalone "Coming Soon" section (moved off the Live page).
 * Clean grid of upcoming singles/albums. Fans can get reminded + pre-save;
 * admins can add/edit/delete releases via the shared ManageModal.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Disc3, Calendar, Loader2, BellRing, Bell, ExternalLink, Plus, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { ManageModal } from '../components/ReleasesStrip';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TYPE_LABEL = { single: 'Single', album: 'Album' };

const countdown = (dateStr) => {
  const target = new Date(`${String(dateStr).slice(0, 10)}T00:00:00Z`).getTime();
  const diff = target - Date.now();
  if (diff <= 0) return 'Out now';
  const d = Math.ceil(diff / 86400000);
  return d >= 1 ? `Drops in ${d} day${d > 1 ? 's' : ''}` : 'Dropping today';
};

const ReleaseGridCard = ({ rel, onNotify, onEdit, isAdmin, busy }) => {
  const outNow = rel.status === 'out_now';
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden hover:border-white/20 transition-colors"
      data-testid={`release-page-card-${rel.id}`}
    >
      <div className="aspect-square w-full bg-gradient-to-br from-[#FF007F]/25 to-[#00F0FF]/20 relative">
        {rel.cover_url
          ? <img src={rel.cover_url} alt={rel.title} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center"><Disc3 className="w-12 h-12 text-white/50" /></div>}
        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-[#00F0FF] text-[10px] font-bold uppercase tracking-wide">
          {TYPE_LABEL[rel.release_type] || 'Release'}
        </span>
        {outNow && (
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-[#39FF14] text-black text-[10px] font-black tracking-wide">OUT NOW</span>
        )}
        {isAdmin && (
          <button onClick={() => onEdit(rel)} className="absolute bottom-2 right-2 px-2 py-1 rounded-full bg-black/60 backdrop-blur text-white/80 text-[10px] font-bold hover:text-white" data-testid={`release-page-edit-${rel.id}`}>
            Edit
          </button>
        )}
      </div>
      <div className="p-3">
        {!outNow && (
          <p className="text-[#39FF14] text-xs font-semibold flex items-center gap-1 mb-1">
            <Calendar className="w-3 h-3" />{countdown(rel.release_date)}
          </p>
        )}
        <p className="text-white text-sm font-bold truncate leading-tight">{rel.title}</p>
        <p className="text-white/50 text-xs truncate mb-2">
          {rel.artist_name || ' '}
          {rel.notify_count > 0 && (
            <span className="ml-1 text-[#FF007F] font-bold" data-testid={`release-page-waiting-${rel.id}`}>· 🔔 {rel.notify_count}</span>
          )}
        </p>
        {rel.description && <p className="text-white/40 text-xs line-clamp-2 mb-3">{rel.description}</p>}
        <div className="flex gap-2">
          <button
            onClick={() => onNotify(rel)}
            disabled={busy === rel.id}
            className={`flex-1 py-2 rounded-full text-xs font-bold flex items-center justify-center gap-1.5 transition-colors ${rel.notified ? 'bg-[#FF007F] text-white' : 'bg-white/10 text-white hover:bg-white/20'}`}
            data-testid={`release-page-notify-${rel.id}`}
          >
            {busy === rel.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (rel.notified ? <BellRing className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />)}
            {rel.notified ? 'Reminding' : 'Remind me'}
          </button>
          {rel.link && (
            <a href={rel.link} target="_blank" rel="noopener noreferrer"
              className="py-2 px-3 rounded-full bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white text-xs font-bold flex items-center gap-1.5"
              data-testid={`release-page-presave-${rel.id}`}>
              <ExternalLink className="w-3.5 h-3.5" />Pre-save
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const ReleasesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = !!(user && (user.isSuperAdmin || user.isAdmin || user.role === 'super_admin' || user.role === 'admin'));
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/api/upcoming-releases`, token ? { headers: { Authorization: `Bearer ${token}` } } : {})
      .then((r) => setReleases(r.data?.releases || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const notify = async (rel) => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/auth'); return; }
    setBusy(rel.id);
    try {
      const res = await axios.post(`${API_URL}/api/upcoming-releases/${rel.id}/notify`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setReleases((rs) => rs.map((r) => r.id === rel.id ? { ...r, notified: res.data.notified, notify_count: res.data.notify_count } : r));
      toast.success(res.data.notified ? "We'll remind you when it drops!" : 'Reminder off');
    } catch { toast.error('Try again'); }
    setBusy(null);
  };

  return (
    <div className="min-h-screen bg-[#050505] pb-24" data-testid="releases-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-[#050505]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="releases-back-btn">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-2 flex-1">
            <Disc3 className="w-5 h-5 text-[#FF007F]" />
            <h1 className="text-white font-bold text-lg tracking-tight" style={{ fontFamily: 'Unbounded' }}>Coming Soon</h1>
          </div>
          {isAdmin && (
            <button onClick={() => setModal('new')} className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-bold" data-testid="release-page-add-btn">
              <Plus className="w-4 h-4" /> Add
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 text-white/40 animate-spin" /></div>
        ) : releases.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="releases-empty">
            <Disc3 className="w-14 h-14 text-white/20 mb-4" />
            <p className="text-white/70 font-semibold">No upcoming releases yet</p>
            <p className="text-white/40 text-sm mt-1">Check back soon for new drops from your favorite artists.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="releases-grid">
            {releases.map((rel) => (
              <ReleaseGridCard key={rel.id} rel={rel} onNotify={notify} onEdit={(r) => setModal(r)} isAdmin={isAdmin} busy={busy} />
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {modal && (
          <ManageModal
            editing={modal === 'new' ? null : modal}
            onClose={() => setModal(null)}
            onSaved={() => { setModal(null); setLoading(true); load(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReleasesPage;
